import { createSlice } from "@reduxjs/toolkit";
import type { MeasureDescriptor, MeasuresStructure } from "../../../../../../api/types/svTypes.ts";
import { MetaItemType } from "../../../../../../api/types/svTypes.ts";
import { updateDataSet } from "../../api/dataSetApiContext";
import { compareSubgroupsByName } from "../../components/builder/common/customMeasure/utilities/measureSubgroups.ts";
import { chartRelatedReportTypes } from "../../utilities/chartRelatedReport.types.ts";
import type { DataSetMetaDataState, MetaDataActionTypes, MetaDataState } from "../types/metaDataState.types";
import { META_DATA_SLICE_NAME } from "../utils/storeUtils";

const dataSetMetaDataEmpty: DataSetMetaDataState = {
  dataSetId: "undefined",
  dimensionsStructure: {
    dimensions: [],
    groups: [],
  },
  measuresStructure: {
    names: [],
    groups: [],
  },
  dimensions: [],
  measures: [],
  allMeasures: [],
  allMetrics: [],
  functions: [],
  measureGroups: [],
  measureSubgroups: [],
  metrics: [],
  metricsStructure: {
    names: [],
    groups: [],
  },
  aggregations: [],
  allAggregations: [],
  currencyDimensions: [],
  customFormulas: [],
  ledgers: [],
};

const initialState: MetaDataState = {
  dataSets: [],
  loaded: false,
  dataSetId: undefined,
};

const addToGroup = (measuresStructure: MeasuresStructure, measure: MeasureDescriptor) => {
  const group = measuresStructure.groups.find((g) => g.caption === measure.group);
  if (group !== undefined) {
    group.names.push(measure.name);
  }
};

const removeFromGroup = (measuresStructure: MeasuresStructure, measure: MeasureDescriptor, groupName: string) => {
  const group = measuresStructure.groups.find((g) => g.caption === groupName);
  if (group !== undefined) {
    const index = group.names.indexOf(measure.name);
    if (index > -1) {
      group.names.splice(index, 1);
    }
  }
};

export const metaDataSlice = createSlice({
  name: META_DATA_SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    update: (state, action: MetaDataActionTypes["update"]) => {
      Object.assign(state, action.payload);
      filterMeasures(state);
    },
    addMeasure: (state, action: MetaDataActionTypes["addMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }

      const measure = action.payload;
      measure.itemType = MetaItemType.MEASURE;
      dataSet.allMeasures.push(measure);
      dataSet.measures.push(measure);
      addToGroup(dataSet.measuresStructure, measure);
    },
    updateMeasure: (state, action: MetaDataActionTypes["updateMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      const index = dataSet.allMeasures.findIndex((m) => m.name === action.payload.name);
      const stateMeasure = dataSet.allMeasures[index];
      if (stateMeasure === undefined) {
        return;
      }
      const updatedMeasure: MeasureDescriptor = { ...action.payload, itemType: MetaItemType.MEASURE };
      dataSet.allMeasures[index] = updatedMeasure;

      if (stateMeasure.group !== updatedMeasure.group) {
        removeFromGroup(dataSet.measuresStructure, stateMeasure, stateMeasure.group);
        addToGroup(dataSet.measuresStructure, updatedMeasure);
      }
      filterMeasures(state);
    },
    addMeasureSubgroup: (state, action: MetaDataActionTypes["addMeasureSubgroup"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined || dataSet.measureSubgroups.some((s) => s.id === action.payload.id)) {
        return;
      }
      dataSet.measureSubgroups.push(action.payload);
      dataSet.measureSubgroups.sort(compareSubgroupsByName);
    },
    renameMeasureSubgroup: (state, action: MetaDataActionTypes["renameMeasureSubgroup"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      const subgroup = dataSet?.measureSubgroups.find((s) => s.id === action.payload.id);
      if (dataSet === undefined || subgroup === undefined) {
        return;
      }
      subgroup.name = action.payload.name;
      dataSet.measureSubgroups.sort(compareSubgroupsByName);
    },
    deleteMeasureSubgroup: (state, action: MetaDataActionTypes["deleteMeasureSubgroup"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      const subgroupId = action.payload;
      dataSet.measureSubgroups = dataSet.measureSubgroups.filter((s) => s.id !== subgroupId);
      dataSet.allMeasures.forEach((measure) => {
        if (measure.subgroupId === subgroupId) {
          delete measure.subgroupId;
        }
      });
      filterMeasures(state);
    },
    updateReportType: (state, action: MetaDataActionTypes["updateReportType"]) => {
      state.reportType = action.payload;
      filterMeasures(state);
    },
    deleteMeasure: (state, action: MetaDataActionTypes["deleteMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      const measure = action.payload;
      const index = dataSet.allMeasures.findIndex((m) => m.name === measure.name);
      if (index < 0) return;
      dataSet.allMeasures.splice(index, 1);
      removeFromGroup(dataSet.measuresStructure, measure, measure.group);
      filterMeasures(state);
    },
    setChartOfAccounts: (state, action: MetaDataActionTypes["setChartOfAccounts"]) => {
      state.chartOfAccounts = action.payload;
    },
    changeDataSet: (state, action: MetaDataActionTypes["changeDataSet"]) => {
      updateDataSetState(state, action.payload);
    },
  },
});

function filterMeasures(state: MetaDataState) {
  for (const dataSet of state.dataSets) {
    if (state.reportType && chartRelatedReportTypes.includes(state.reportType)) {
      dataSet.measures = getChartAvailableMeasures(dataSet);
    } else {
      dataSet.measures = dataSet.allMeasures;
    }
    dataSet.metrics = dataSet.allMetrics;
  }
}
const updateDataSetState = (state: MetaDataState, dataSetId: string) => {
  state.dataSetId = dataSetId;
  // Update the data set in the API context to ensure it is used in the API calls
  updateDataSet(dataSetId || "");
};

const getChartAvailableMeasures = (state: DataSetMetaDataState) => {
  return state.allMeasures.filter((measure) => !measure.capabilities.isScalar);
};

export { dataSetMetaDataEmpty };

export default metaDataSlice.reducer;
